<template>
    <ValidationObserver ref="form" v-slot="{ invalid }">
        <div class="absolute top-0 left-0 right-0 bottom-0 w-full overflow-hidden flex items-center text-center justify-center z-50" v-show="loading"><spinner line-fg-color="#922C49" size="55" :line-size="5"></spinner></div>
        <form id="form" v-if="!loading" @submit.prevent="submitForm" class="flex flex-col h-full" autocomplete="off">
            <page-header :buttons="pageActions.buttons" :additional="pageActions.additional" @method="methodHandler">
                <template slot="title">{{ type }} Member<span v-if="item && type === 'Edit'"> - {{ item.first + ' ' + item.last }}</span></template>
            </page-header>

            <tabs :tabs="tabs" :active="startTab" @setTab="setActiveTab" ref="tabs"></tabs>
            <div class="bg-red-200 py-2 text-sm text-red-700 text-center w-full -mt-px" role="alert" v-show="invalid && submitted" >Please complete all required fields</div>
            <div class="bg-red-200 py-2 text-sm text-red-700 text-center w-full -mt-px" v-if="errorMessage">{{ errorMessage }}</div>

            <div class="flex md:flex-row sm:flex-col flex-grow">
                <div class="md:w-3/4 sm:w-full p-4 sm:p-6 lg:px-6 bg-gray-200 border-r border-gray-400" v-show="activeTab === 'details'">
                    <ValidationObserver vid="details" :slim="true">
                        <div class="grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12">
                            <input-text class="sm:col-span-4"
                                label="Member ID"
                                name="member_id"
                                placeholder="Member ID"
                                v-model="item.member_id"
                                :required="type!=='New'"
                                :disabled="type==='New'"
                                :readonly="type==='New'"
                            />
                            <input-select class="sm:col-span-4"
                                label="Member Type"
                                name="member_type"
                                placeholder="Member Type"
                                :options="options.types"
                                v-model="item.member_type_id"
                                :initial-value="initialValue.type"
                                rules="required"
                                :required="true" />
                            <input-select class="sm:col-span-4"
                                placeholder="Search Members ..."
                                label="Associated with Member"
                                name="member_parent_id"
                                v-model="item.member_parent_id"
                                :initialValue="initialValue.parent"
                                return="value"
                                :endpoint="'members'"
                                endpointLabelField="label"
                                :required="false"/>
                            <input-text class="sm:col-span-4"
                                label="First Name"
                                placeholder="First Name"
                                name="first"
                                v-model="item.first"
                                rules="required"
                                :required="true" />
                            <input-text class="sm:col-span-4"
                                label="Middle Name"
                                placeholder="Middle Name"
                                name="last"
                                v-model="item.middle"
                                :required="false" />
                            <input-text class="sm:col-span-4"
                                label="Last Name"
                                placeholder="Last Name"
                                name="last"
                                v-model="item.last"
                                rules="required"
                                :required="true" />
                            <input-text class="sm:col-span-3"
                                label="Other First Name"
                                placeholder="Other First Name"
                                name="other_first"
                                v-model="item.other_first"
                                :required="false" />
                            <input-text class="sm:col-span-3"
                                label="Other Last Name"
                                placeholder="Other Last Name"
                                name="other_last"
                                v-model="item.other_last"
                                :required="false" />
                            <input-text class="sm:col-span-3"
                                label="Email"
                                placeholder="Email"
                                name="email"
                                v-model="item.email"
                                rules="email"
                                :required="false" />
                            <input-text class="sm:col-span-3"
                                label="Mobile"
                                placeholder="Mobile"
                                name="mobile"
                                v-model="item.mobile"
                                :required="false"
                                :mask="'#### ### ###'"/>
                            <input-text class="sm:col-span-3"
                                label="Date of Birth"
                                placeholder="DD/MM/YYYY"
                                name="date_of_birth"
                                v-model="item.date_of_birth"
                                :required="false"
                                :mask="'##/##/####'"/>
                            <input-select class="sm:col-span-3"
                                label="Gender"
                                name="gender_id"
                                :options="options.genders"
                                v-model="item.gender_id"
                                :initial-value="initialValue.gender"
                                rules="required"
                                :required="true" />
                            <input-textarea class="col-span-12"
                                label="Important Information"
                                name="important_information"
                                v-model="item.important_information"
                            />
                        </div>
                        <div class="mt-2 grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12">
                            <h2 class="w-full sm:col-span-12 py-2 border-b-2 border-charcoal-900 text-left font-medium text-charcoal-900">Residential Address</h2>
                            <input-text class="sm:col-span-6"
                                label="Address"
                                name="address"
                                placeholder="Address"
                                v-model="item.address"
                                :required="false" />
                            <input-text class="sm:col-span-6"
                                label="Suburb"
                                name="suburb"
                                placeholder="Suburb"
                                v-model="item.suburb"
                                :required="false" />
                            <input-text class="sm:col-span-4"
                                label="Postcode"
                                name="postcode"
                                placeholder="Postcode"
                                v-model="item.postcode"
                                :required="false" />
                            <input-select class="sm:col-span-4"
                                label="State"
                                name="state"
                                placeholder="State"
                                :options="options.states"
                                v-model="item.state"
                                :initial-value="initialValue.state"
                                :required="false"
                                return="label"/>
                            <input-text class="sm:col-span-4"
                                label="Country"
                                name="country"
                                placeholder="Country"
                                v-model="item.country"
                                :required="false" />
                        </div>
                        <div class="mt-2 grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12">
                            <h2 class="w-full sm:col-span-12 py-2 border-b-2 border-charcoal-900 text-left font-medium text-charcoal-900">Postal Address <small class="text-xs">(If different to Residential Address)</small></h2>
                            <input-text class="sm:col-span-6"
                                label="Address"
                                name="address"
                                placeholder="Address"
                                v-model="item.postal_address"
                                :required="false" />
                            <input-text class="sm:col-span-6"
                                label="Suburb"
                                name="suburb"
                                placeholder="Suburb"
                                v-model="item.postal_suburb"
                                :required="false" />
                            <input-text class="sm:col-span-4"
                                label="Postcode"
                                name="postcode"
                                placeholder="Postcode"
                                v-model="item.postal_postcode"
                                :required="false" />
                            <input-select class="sm:col-span-4"
                                label="State"
                                name="postal_state"
                                placeholder="State"
                                :options="options.states"
                                v-model="item.postal_state"
                                :initial-value="initialValue.postal_state"
                                :required="false"
                                return="label"/>
                            <input-text class="sm:col-span-4"
                                label="Country"
                                name="country"
                                placeholder="Country"
                                v-model="item.postal_country"
                                :required="false" />
                        </div>
                        <div class="mt-2 grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12">
                            <h2 class="w-full sm:col-span-12 py-2 border-b-2 border-charcoal-900 text-left font-medium text-charcoal-900">Bank Account</h2>
                            <input-text class="sm:col-span-3"
                                label="Account Name"
                                placeholder="Account Name"
                                name="account_name"
                                v-model="item.account_name"
                                :required="false"
                                :max-length="32"
                            />
                            <input-text class="sm:col-span-3"
                                label="BSB"
                                name="bsb"
                                placeholder="BSB"
                                v-model="item.account_bsb"
                                :required="false" />
                            <input-text class="sm:col-span-3"
                                label="Account Number"
                                name="account_number"
                                placeholder="Account Number"
                                v-model="item.account_number"
                                :required="false" />
                            <input-toggle class="sm:col-span-3"
                                label="Member Bank Account"
                                name="member_bank_account"
                                v-model="item.member_bank_account"
                                :required="false" />
                        </div>
                        <div class="grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12">
                            <h2 class="w-full sm:col-span-12 py-2 border-b-2 border-charcoal-900 text-left font-medium text-charcoal-900">Password</h2>
                            <input-text class="sm:col-span-6"
                                type="password"
                                name="password"
                                label="Password"
                                :placeholder="type !== 'New' ? 'Leave empty for no change' : 'Password'"
                                v-model="item.password"
                                :rules="type === 'New' ? 'password:@confirm' : 'password:@confirm'"
                                :required="false" />
                            <input-text class="sm:col-span-6"
                                type="password"
                                name="confirm"
                                label="Confirm Password"
                                provider-name="confirm"
                                :placeholder="type !== 'New' ? 'Leave empty for no change' : 'Confirm Password'"
                                :rules="type === 'New' ? '' : ''"
                                :required="false" />
                        </div>
                        <div class="mt-2">
                            <h2 class="w-full sm:col-span-12 py-2 border-b-2 border-charcoal-900 text-left font-medium text-charcoal-900 mb-3">Identification</h2>
                            <span v-for="(file, index) in item.identification_files" class="text-base mb-1 block flex flex-row items-center my-2">
                                <a :href="s3URL + '/' + file.path " target="_blank" class="text-gray-800 hover:text-default-500 truncate">{{ file.filename }}</a>
                                <button type="button" @click="removeUploadFile(index, 'identification_files')">
                                    <svg aria-hidden="true" focusable="false" data-prefix="far" data-icon="times" class="svg-inline--fa fa-times w-6 h-6 text-red-700 ml-2" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                                        <path fill="currentColor" d="M207.6 256l107.72-107.72c6.23-6.23 6.23-16.34 0-22.58l-25.03-25.03c-6.23-6.23-16.34-6.23-22.58 0L160 208.4 52.28 100.68c-6.23-6.23-16.34-6.23-22.58 0L4.68 125.7c-6.23 6.23-6.23 16.34 0 22.58L112.4 256 4.68 363.72c-6.23 6.23-6.23 16.34 0 22.58l25.03 25.03c6.23 6.23 16.34 6.23 22.58 0L160 303.6l107.72 107.72c6.23 6.23 16.34 6.23 22.58 0l25.03-25.03c6.23-6.23 6.23-16.34 0-22.58L207.6 256z"></path>
                                    </svg>
                                </button>
                            </span>
                            <FileUpload @uploaded="fileUploaded($event, 'identification_files')"/>
                        </div>
                        <div class="mb-10 mt-2">
                            <h2 class="w-full sm:col-span-12 py-2 border-b-2 border-charcoal-900 text-left font-medium text-charcoal-900 mb-3">Cards</h2>
                            <span v-for="(file, index) in item.cards" class="text-base mb-1 block flex flex-row items-center my-2">
                                <div v-if="file.filename.search('jpeg') || file.filename.search('jpg') || file.filename.search('png')" class="flex flex-row">
                                    <img :src="s3URL + '/' + file.path"  alt=""/>
                                    <button type="button" @click="removeUploadFile(index, 'cards')">
                                        <svg aria-hidden="true" focusable="false" data-prefix="far" data-icon="times" class="svg-inline--fa fa-times w-6 h-6 text-red-700 ml-2" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                                            <path fill="currentColor" d="M207.6 256l107.72-107.72c6.23-6.23 6.23-16.34 0-22.58l-25.03-25.03c-6.23-6.23-16.34-6.23-22.58 0L160 208.4 52.28 100.68c-6.23-6.23-16.34-6.23-22.58 0L4.68 125.7c-6.23 6.23-6.23 16.34 0 22.58L112.4 256 4.68 363.72c-6.23 6.23-6.23 16.34 0 22.58l25.03 25.03c6.23 6.23 16.34 6.23 22.58 0L160 303.6l107.72 107.72c6.23 6.23 16.34 6.23 22.58 0l25.03-25.03c6.23-6.23 6.23-16.34 0-22.58L207.6 256z"></path>
                                        </svg>
                                    </button>
                                </div>
                                <span v-else>
                                    <a :href="s3URL + '/' + file.path " target="_blank" class="text-gray-800 hover:text-default-500 truncate">{{ file.filename }}</a>
                                    <button type="button" @click="removeUploadFile(index, 'cards')">
                                        <svg aria-hidden="true" focusable="false" data-prefix="far" data-icon="times" class="svg-inline--fa fa-times w-6 h-6 text-red-700 ml-2" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                                            <path fill="currentColor" d="M207.6 256l107.72-107.72c6.23-6.23 6.23-16.34 0-22.58l-25.03-25.03c-6.23-6.23-16.34-6.23-22.58 0L160 208.4 52.28 100.68c-6.23-6.23-16.34-6.23-22.58 0L4.68 125.7c-6.23 6.23-6.23 16.34 0 22.58L112.4 256 4.68 363.72c-6.23 6.23-6.23 16.34 0 22.58l25.03 25.03c6.23 6.23 16.34 6.23 22.58 0L160 303.6l107.72 107.72c6.23 6.23 16.34 6.23 22.58 0l25.03-25.03c6.23-6.23 6.23-16.34 0-22.58L207.6 256z"></path>
                                        </svg>
                                    </button>
                                </span>
                            </span>
                            <FileUpload @uploaded="fileUploaded($event, 'cards')"/>
                        </div>

                    </ValidationObserver>
                </div>
                <div class="md:w-3/4 sm:w-full p-4 sm:p-6 lg:px-6 bg-gray-200 border-r border-gray-400" v-show="activeTab === 'custom-fields'">
                    <ValidationObserver vid="details" :slim="true">
                        <div class="grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12">
                            <div v-for="(field, index) in item.custom_fields" :key="index"
                                class="flex flex-col cursor-pointer relative"
                                :class="[ 'col-span-'+field.width ]">
                                <label class="block text-sm font-medium leading-5 text-gray-800 relative mb-1 cursor-pointer"
                                    :class="[field.required ? 'required' : '']"
                                    v-if="field.type !== 'heading'"
                                >{{ field.label }}</label>
                                <div class="pb-4">
                                    <template v-if="field.type === 'textInput'">
                                        <input type="text" v-model="field.answer" :placeholder="field.placeholder ? field.placeholder.value : ''" class="form-input block w-full transition duration-150 ease-in-out sm:text-sm leading-none text-gray-700" />
                                    </template>
                                    <template v-if="field.type === 'longTextInput'">
                                        <textarea rows="5" v-model="field.answer" :placeholder="field.placeholder ? field.placeholder.value : ''" class="form-input block w-full transition duration-150 ease-in-out sm:text-sm leading-none text-gray-700" ></textarea>
                                    </template>
                                    <template v-if="field.type === 'numberInput'">
                                        <input type="number" v-model="field.answer" :placeholder="field.placeholder ? field.placeholder.value : ''" class="form-input block w-full transition duration-150 ease-in-out sm:text-sm leading-none text-gray-700" />
                                    </template>
                                    <template v-if="field.type === 'selectInput'">
                                        <select class="border-gray-500 border py-2 px-2 block w-full transition duration-150 ease-in-out text-sm leading-none text-gray-700" v-model="field.answer">
                                            <option value="" v-if="field.placeholder" disabled selected hidden>{{ field.placeholder.value }}</option>
                                            <option v-for="(option, optionIndex) in field.options" :value="option.value ? option.value : option.label" :key="optionIndex">{{ option.label }}</option>
                                        </select>
                                    </template>
                                    <template v-if="field.type === 'radioInput'">
                                        <div class="flex" :class="[ field.display === 'vertical' ? 'flex-col' : 'flex-row flex-wrap' ]">
                                            <label v-for="(option, optionIndex) in field.options" :key="optionIndex"
                                                class="mr-4 flex flex-row items-center cursor-pointer"
                                                :class="[ field.display === 'vertical' ? 'mb-2' : 'w-full' ]"
                                            >
                                                <div class="mb-2 flex items-center">
                                                    <input type="radio" :value="option.value ? option.value : option.label" v-model="field.answer" :name="field.id" class="w-5 h-5"><span class="ml-1 text-sm">{{ option.label }}</span>
                                                </div>
                                            </label>
                                        </div>
                                    </template>
                                    <template v-if="field.type === 'checkboxInput'">
                                        <div class="flex " :class="[ field.display === 'vertical' ? 'flex-col' : 'flex-row flex-wrap' ]">
                                            <label v-for="(option, optionIndex) in field.options" :key="optionIndex"
                                                class="mr-4 flex flex-row items-center cursor-pointer"
                                                :class="[ field.display === 'vertical' ? 'mb-2' : 'w-full' ]"
                                            >
                                                <div class="mb-2 flex items-center">
                                                    <input type="checkbox" :value="option.value ? option.value : option.label" v-model="field.answer" :id="optionIndex" class="w-4 h-4"><span class="ml-1 text-sm">{{ option.label }}</span>
                                                </div>
                                            </label>
                                        </div>
                                    </template>
                                    <template v-if="field.type === 'fileUpload'">
                                        <button type="button" class="float-left bg-default-500 text-white text-xs py-1 px-3 opacity-50 cursor-not-allowed">
                                            <font-awesome-icon class="text-white text-sm mr-2" :icon="['fal', 'upload']" />
                                            {{ field.button_label ? field.button_label : 'Upload' }}
                                        </button>
                                    </template>
                                    <template v-if="field.type === 'dateInput'">
                                        <input-date-time
                                            :placeholder="field.placeholder ? field.placeholder.value : ''"
                                            :name="field.label"
                                            v-model="field.answer"
                                            :initialValue="field.answer"
                                            type="date"
                                            format="dd/MM/yyyy"
                                            :auto="true"
                                            :required="field.required"
                                            :margin-top="'mt-0'"
                                        />
                                    </template>
                                    <template v-if="field.type === 'heading'">
                                        <div class="block pt-4 font-bold">{{ field.label }}</div>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </ValidationObserver>
                </div>

                <div class="md:w-1/4 sm:w-full px-4 sm:px-6 lg:px-6">
                    <ValidationObserver vid="details" :slim="true">
                        <div class="mt-6 grid grid-cols-1 row-gap-5 col-gap-4 sm:grid-cols-12">
                            <input-toggle class="sm:col-span-12"
                                label="Active"
                                name="active"
                                v-model="item.active"
                                :required="false" />
                            <input-toggle class="sm:col-span-12"
                                label="Other Name Preferred"
                                name="other_name_preferred"
                                v-model="item.other_name_preferred"
                                :required="false" />
                        </div>
                    </ValidationObserver>
                </div>
            </div>
        </form>
    </ValidationObserver>
</template>

<script>
    import axios from "axios";
    import FormSetup from "../../mixins/Tabs";
    import FormButtons from "../../mixins/FormButtons";
    import FileUpload from '@/components/FileUpload'
    import { matchingStateOption, stateSelectOption } from "../../helpers/australianStates";
    export default {
        name: "MemberForm",
        components: {
            FileUpload
        },
        props: {
            id: {required: false},
            type: {required: true},
            return: {required: false, default: '/members'},
            page: {required: false, default: 'members'}
        },
        mixins: [
            FormSetup,
            FormButtons
        ],
        data: function () {
            return {
                resource: 'members',
                prefix: '',
                pageActions: {
                    buttons: [
                        {
                            label: 'Save',
                            icon: 'save',
                            iconClass: 'text-green-600',
                            type: 'method',
                            method: 'save',
                        },
                        {
                            label: 'View',
                            icon: 'eye',
                            iconClass: 'text-blue-600',
                            type: 'method',
                            method: 'view',
                            prefix: '/members',
                            display: this.type === "Edit"
                        },
                        {
                            label: 'Cancel',
                            icon: 'times',
                            iconClass: 'text-red-600',
                            type: 'method',
                            method: 'cancel',
                        }
                    ],
                    additional: []
                },
                tabs: [
                    {
                        name: 'Details',
                        slug: 'details'
                    },
                    {
                        name: 'Custom Fields',
                        slug: 'custom-fields'
                    },
                ],
                startTab: null,
                loading: true,
                submitted: false,
                errorMessage: '',
                showPostalAddress: 'hide',
                item: {
                    member_id: '',
                    member_type_id:'',
                    member_parent_id: '',
                    date_of_birth: '',
                    gender_id: '',
                    first: '',
                    last: '',
                    other_first: '',
                    other_last: '',
                    other_name_preferred: false,
                    mobile: '',
                    email: '',
                    account_name: '',
                    address: '',
                    suburb: '',
                    postcode: '',
                    state: '',
                    account_number: '',
                    postal_address: '',
                    postal_suburb: '',
                    postal_postcode: '',
                    postal_state: '',
                    postal_country: '',
                    member_bank_account: false,
                    identification_files: [],
                    cards: [],
                },
                s3URL: process.env.VUE_APP_S3_URL,
                options: {
                    types: [],
                    genders: [],
                    states: []
                }
            }
        },
        methods: {
            getData: function () {
                this.$Progress.start();

                axios.get(process.env.VUE_APP_API_URL + '/members/' + this.id)
                    .then(response => {
                        this.item = response.data;
                        this.$Progress.finish();
                        this.loading = false;
                    })
                    .catch(() => {
                        this.$Progress.finish();
                        this.loading = false;
                    });
            },
            fileUploaded(payload, key) {
                if (!this.item[key]){
                    this.item[key] = [];
                }

                payload.data.forEach(file => {
                    this.item[key].push(file)
                })
            },
            removeUploadFile(index, key){
                this.item[key].splice(index, 1)
            },
            submitForm: function () {
                this.submitted = false;

                this.$refs.form.validate().then(success => {
                    let _method;
                    let _url;
                    if (!success) {
                        this.submitted = true;
                        return;
                    }

                    this.submitted = true;

                    this.$store.commit('TOGGLE_BACKDROP', {open: true});
                    this.errorMessage = '';

                    if (this.id) {
                        _url = process.env.VUE_APP_API_URL + '/members/' + this.id;
                        _method = 'PUT';
                    } else if (this.item.id) {
                        _url = process.env.VUE_APP_API_URL + '/members/' + this.item.id;
                        _method = 'PUT';
                    } else {
                        _url = process.env.VUE_APP_API_URL + '/members';
                        _method = 'POST';
                    }
                    axios({
                        method: _method,
                        url: _url,
                        data: this.item,
                    }).then(response => {
                        if (response.data.success) {
                            this.$store.commit('TOGGLE_BACKDROP', {open: false});

                            if (this.saveClose) {
                                if (this.return) {
                                    this.$router.push(this.return);
                                } else {
                                    this.$router.push('/members');
                                }
                            } else {
                                this.$router.push('/members/'+response.data.data.id+'/view');
                                //this.item.id = response.data.data.id;
                            }

                            this.$notify({
                                group: 'main',
                                type: 'success',
                                title: 'Success',
                                text: response.data.message,
                                duration: 5000
                            });

                        } else {
                            if (response.data.message) {
                                this.errorMessage = response.data.message;
                            }
                            this.$store.commit('TOGGLE_BACKDROP', {open: false});
                        }
                    }).catch(error => {
                        if (error.response.data.message) {
                            this.errorMessage = error.response.data.message;
                        } else {
                            this.errorMessage = 'An error occurred. Please contact the System Administrator.';
                        }

                        this.$store.commit('TOGGLE_BACKDROP', {open: false});
                    });
                });
            },
            cancel: function () {
                this.$router.push(this.return);
            },
            methodHandler: function (method) {
                this[method]();
            },
            actionHandler: function (data) {
                this[data.method](data);
            }
        },
        beforeMount() {
            // Types
            if (this.$store.getters.memberTypes.length === 0) {
                this.$store.dispatch('GET_MEMBER_TYPES').then(() => {
                    this.options.types = this.$store.getters.memberTypes.map(function (type) {
                        return {label: type.name, value: type.id};
                    });
                });
            } else {
                this.options.types = this.$store.getters.memberTypes.map(function (type) {
                    return {label: type.name, value: type.id};
                });
            }
            // Genders
            if (this.$store.getters.genderTypes.length === 0) {
                this.$store.dispatch('GET_GENDER_TYPES').then(() => {
                    this.options.genders = this.$store.getters.genderTypes.map(function (type) {
                        return {label: type.name, value: type.id};
                    });
                });
            } else {
                this.options.genders = this.$store.getters.genderTypes.map(function (type) {
                    return {label: type.name, value: type.id};
                });
            }
            // States
            if (this.$store.getters.states.length === 0) {
                this.$store.dispatch('GET_STATES').then(() => {
                    this.options.states = this.$store.getters.states.map(stateSelectOption);
                });
            } else {
                this.options.states = this.$store.getters.states.map(stateSelectOption);
            }
        },
        mounted() {
            if (this.type === "New") {
                this.loading = false;
                this.item.active = true;
                this.$Progress.finish();
                // Se defaults here
                this.item.member_bank_account = true;
            } else if (this.type === "Edit") {
                this.pageActions.additional.push({
                    label: 'Delete',
                    icon: 'trash-alt',
                    type: 'method',
                    method: 'deleteModal',
                    iconClass: 'text-red-500',
                    display: !!(this.$store.getters.permissions.indexOf('members.destroy') !== -1 || this.$store.getters.nimdarepus)
                });
                this.getData();
            }

            const params = new URLSearchParams(window.location.search)
            let windowLocationHash = window.location.hash.replace('#', '');

            if (windowLocationHash) {
                this.startTab = windowLocationHash;
            }
        },
        computed: {
            initialValue: function(){
                let output = {};

                if (this.item && this.item.gender){
                    output['gender'] = this.item.gender.id;
                } else {
                    output['gender'] =  null
                }

                if (this.item && this.item.type){
                    output['type'] = this.item.type.id;
                } else {
                    output['type'] =  null
                }

                if (this.item && this.item.state){
                    output['state'] = matchingStateOption(this.options.states, this.item.state)?.value ?? null;
                } else {
                    output['state'] =  null
                }

                if (this.item && this.item.postal_state){
                    output['postal_state'] = matchingStateOption(this.options.states, this.item.postal_state)?.value ?? null;
                } else {
                    output['postal_state'] =  null
                }

                if (this.item && this.item.parent){
                    output['parent'] = { label: `${this.item.parent.first} ${this.item.parent.last}` + ' (' + this.item.parent.member_id + ')', value: this.item.parent.id };
                } else {
                    output['parent'] =  null
                }

                return output;
            }
        }
    }
</script>

<style scoped>

</style>
